// Shop All mobile bottom Filter / Sort bar — winner of the "Optimizing the bottom filter"
// A/B test (Convert, Sept 2026), shipped to all mWeb users. Self-contained: this file
// injects its own styles and icons and changes no other theme file.
//
// What it does, on /collections/shop-all only (below 768px):
//   • the sticky Filter / Sort bar sits flush at the bottom as a full-width pink sheet
//   • the bottom nav bar is hidden on this page
//   • filter and sort icons are added to the bar
//
// Gated on .searchtap-collection-product-grid--shop-all, the modifier class
// sections/searchtap-collection-product-grid.liquid puts on the Shop All grid only, so
// loading this file anywhere else does nothing.
//
// The bar is drawn in the browser by the SearchTap Vue bundle (assets/searchtap.js), so
// there is no Liquid to put the icons in — they are inserted after SearchTap renders and
// re-inserted whenever Vue redraws and drops them. MutationObserver callbacks run before
// the next paint, so the icons never visibly pop in. SearchTap renders the bar twice
// (the pre-API <initial-toolbox-bar> and the SearchPage one) and labels it "FIlter" in
// one of them, hence the case-insensitive match.
//
// Unlike the A/B test script, the bar is never hidden while waiting: if SearchTap's
// markup changes, the bar simply shows without icons instead of staying invisible.
(() => {
  'use strict';

  const ROOT_SELECTOR = '.searchtap-collection-product-grid--shop-all';
  const BAR_SELECTOR = '#sortFilter';
  const ICON_ATTR = 'data-shopall-filter-bar-icon';
  const STYLE_ID = 'shopall-filter-bar-styles';

  // Every rule is scoped to the Shop All grid and to mobile, so nothing leaks to other
  // collections or to desktop. The bottom nav lives outside the grid, hence the
  // body:has() scope there (the nav is display:none above 767px anyway).
  const CSS = `
@media (max-width: 767px) {
  body:has(${ROOT_SELECTOR}) .bottom-nav-bar.bottom-nav-bar-parent {
    display: none !important;
  }

  ${ROOT_SELECTOR} .st-filter-bar {
    bottom: 0 !important;
    z-index: 99;
    transition: none !important;
  }

  ${ROOT_SELECTOR} div#sortFilter {
    width: 100% !important;
    border: 1.5px solid #fff2f4;
    border-radius: 12px 12px 0 0;
    background: #fff2f4;
    color: #fd41a7;
    box-shadow: 0 -6px 21.5px 0 rgba(0, 0, 0, 0.13);
  }

  ${ROOT_SELECTOR} #sortFilter span.disInBlock {
    width: 100% !important;
  }

  ${ROOT_SELECTOR} #sortFilter span.disInBlock span.st-flex {
    width: 50%;
    align-items: center;
    justify-content: center;
  }

  ${ROOT_SELECTOR} div#sortFilter .filter_h span.disInBlock span,
  ${ROOT_SELECTOR} #sortFilter .sort_h,
  ${ROOT_SELECTOR} #sortFilter .sort_h div {
    color: #fd41a7;
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  ${ROOT_SELECTOR} #sortFilter .filter_h {
    padding-left: 0 !important;
  }

  ${ROOT_SELECTOR} #sortFilter .sort_h {
    position: relative !important;
    display: flex !important;
    align-items: center !important;
  }

  ${ROOT_SELECTOR} #sortFilter .partition {
    border-left: 1px solid #fdcbe6;
  }

  /* The old background-image icons give way to the inline SVGs. */
  ${ROOT_SELECTOR} div#sortFilter .filter_h span.disInBlock span::before,
  ${ROOT_SELECTOR} .sortFilterCon div.sort_h::after {
    content: none !important;
  }

  ${ROOT_SELECTOR} .shopall-filter-bar__icon--filter {
    display: block !important;
    flex-shrink: 0 !important;
    width: 21px !important;
    height: 17px !important;
    margin-right: 6px !important;
  }

  ${ROOT_SELECTOR} .shopall-filter-bar__icon--sort {
    position: absolute !important;
    top: 50% !important;
    left: auto !important;
    margin-left: 50px !important;
    -webkit-transform: translateY(-50%) translateZ(0) !important;
    transform: translateY(-50%) translateZ(0) !important;
    -webkit-backface-visibility: hidden !important;
    backface-visibility: hidden !important;
    will-change: transform !important;
    pointer-events: none !important;
  }

  /* Selected-filter count bubble */
  ${ROOT_SELECTOR} #sortFilter .filter_h span.st-absolute {
    top: 14px;
    left: 36%;
    width: 16px;
    height: 16px;
    background-color: #fd41a7;
    color: #fff !important; /* beats the pink label colour above if nested in it */
    font-size: 9px;
    font-weight: 500;
    line-height: 16px;
    text-align: center;
  }

  /* "Sort applied" dot */
  ${ROOT_SELECTOR} #sortFilter .sort-applied-ellip {
    right: 24% !important;
    background-color: #fd41a7 !important;
    line-height: 16px;
    text-align: center;
  }
}`;

  const ICONS = {
    filter:
      '<svg class="shopall-filter-bar__icon shopall-filter-bar__icon--filter" xmlns="http://www.w3.org/2000/svg" width="21" height="17" viewBox="0 0 21 17" fill="none" aria-hidden="true" focusable="false"><path fill="#FD41A7" d="M0.65625 3.42577H11.3414C11.6361 4.62953 12.7133 5.52478 13.994 5.52478C15.2746 5.52478 16.3518 4.62953 16.6466 3.42577H20.3438C20.7062 3.42577 21 3.12875 21 2.76241C21 2.39607 20.7062 2.09905 20.3438 2.09905H16.6465C16.3518 0.895291 15.2746 0 13.9939 0C12.7132 0 11.636 0.895291 11.3413 2.09905H0.65625C0.293836 2.09905 0 2.39607 0 2.76241C0 3.12875 0.293836 3.42577 0.65625 3.42577ZM13.994 1.32673C14.7771 1.32673 15.4142 1.97077 15.4142 2.76237C15.4142 3.55401 14.7771 4.19805 13.994 4.19805C13.2108 4.19805 12.5737 3.55401 12.5737 2.76237C12.5737 1.97077 13.2108 1.32673 13.994 1.32673ZM0.65625 9.16336H4.35348C4.64822 10.3671 5.72541 11.2624 7.00608 11.2624C8.28676 11.2624 9.36395 10.3671 9.65869 9.16336H20.3438C20.7062 9.16336 21 8.86634 21 8.5C21 8.13366 20.7062 7.83664 20.3438 7.83664H9.65865C9.36391 6.63288 8.28671 5.73759 7.00604 5.73759C5.72537 5.73759 4.64818 6.63288 4.35344 7.83664H0.65625C0.293836 7.83664 0 8.13366 0 8.5C0 8.86634 0.293795 9.16336 0.65625 9.16336ZM7.00604 7.06432C7.7892 7.06432 8.42633 7.70836 8.42633 8.5C8.42633 9.2916 7.7892 9.93564 7.00604 9.93564C6.22289 9.93564 5.58575 9.2916 5.58575 8.5C5.58575 7.70836 6.22289 7.06432 7.00604 7.06432ZM20.3438 13.5742H16.6465C16.3518 12.3705 15.2746 11.4752 13.9939 11.4752C12.7132 11.4752 11.6361 12.3705 11.3413 13.5742H0.65625C0.293836 13.5742 0 13.8712 0 14.2376C0 14.6039 0.293836 14.901 0.65625 14.901H11.3414C11.6361 16.1047 12.7133 17 13.994 17C15.2746 17 16.3518 16.1047 16.6466 14.901H20.3438C20.7062 14.901 21 14.6039 21 14.2376C21 13.8712 20.7062 13.5742 20.3438 13.5742ZM13.994 15.6733C13.2108 15.6733 12.5737 15.0292 12.5737 14.2376C12.5737 13.446 13.2108 12.8019 13.994 12.8019C14.7771 12.8019 15.4142 13.446 15.4142 14.2376C15.4142 15.0292 14.7771 15.6733 13.994 15.6733Z"/></svg>',
    sort:
      '<svg class="shopall-filter-bar__icon shopall-filter-bar__icon--sort" xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true" focusable="false"><path fill="#FD41A7" d="M4.99998 6C4.82076 6 4.64157 5.93998 4.50493 5.82018L0.205142 1.0486C-0.0683806 0.808678 -0.0683806 0.419689 0.205142 0.179867C0.478554 -0.0599556 0.921935 -0.0599556 1.19548 0.179867L4.99998 4.51717L8.80451 0.179983C9.07803 -0.0598391 9.52137 -0.0598391 9.79476 0.179983C10.0684 0.419806 10.0684 0.808794 9.79476 1.04871L5.49504 5.8203C5.35834 5.94011 5.17914 6 4.99998 6Z"/></svg>',
  };

  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  };

  const createIcon = (name) => {
    const template = document.createElement('template');
    template.innerHTML = ICONS[name];
    const svg = template.content.firstElementChild;
    svg.setAttribute(ICON_ATTR, name);
    return svg;
  };

  // The label is the innermost span whose text reads "Filter". With no count bubble its
  // wrapper (span.disInBlock) reads "Filter" too, and it comes first in document order —
  // so take the last match, which is the deepest.
  const findFilterLabel = (bar) =>
    [...bar.querySelectorAll('.filter_h span')]
      .filter((span) => span.textContent.trim().toLowerCase() === 'filter')
      .pop();

  const addIcons = (root) => {
    root.querySelectorAll(BAR_SELECTOR).forEach((bar) => {
      const filterLabel = findFilterLabel(bar);
      if (filterLabel && !filterLabel.querySelector(`[${ICON_ATTR}="filter"]`)) {
        filterLabel.prepend(createIcon('filter'));
      }

      const sortLabel = bar.querySelector('.sort_h > div:first-child');
      if (sortLabel && !sortLabel.parentElement.querySelector(`[${ICON_ATTR}="sort"]`)) {
        sortLabel.after(createIcon('sort'));
      }
    });
  };

  const init = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return; // not the Shop All page

    injectStyles();
    addIcons(root);

    // Kept running (not disconnected once the icons are in): Vue re-renders the bar on
    // filter / sort changes and can drop the inserted icons. Our own insertions trigger
    // one more pass that finds both icons present and changes nothing.
    new MutationObserver(() => addIcons(root)).observe(root, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
